// src/mocks/handlers/sources/commercialTermsHandler.ts

import { rest } from 'msw';
import commercialTerms from '@/mocks/data/commercialTerms.json';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { VITE_MSW_BASE_URL } from '@/config';
import {
  ICommercialTermsResponse,
  TCommercialTermSortBy,
} from '@/domains/sources/sources.types';
import { API_DEFAULT_PAGE_SIZE } from '@/services/api/api.constants';
import { isAccessTokenExpired } from '@/mocks/lib/authTokenUtils';
import { matchesAgencyFilter } from '@/mocks/lib/matchesAgencyFilter';

export const commercialTermsHandler = rest.get<ICommercialTermsResponse>(
  `${VITE_MSW_BASE_URL}${API_ENDPOINTS.SOURCES_COMMERCIAL_TERMS}`,
  (req, res, ctx) => {
    const token = req.headers.get('Authorization');
    if (!token) {
      return res(ctx.status(500), ctx.json({ detail: 'Something went wrong' }));
    }

    if (isAccessTokenExpired()) {
      return res(ctx.status(401), ctx.json({ error: 'Access token expired' }));
    }

    const { customer_uuid } = req.params;

    const url = new URL(req.url);
    const params = url.searchParams;

    const status = params.get('statuses');
    const currency = params.get('currencies');
    const network = params.get('networks'); // maps to network_name
    const agencies = params.getAll('agencies');

    const ordering = params.get('ordering') || '';
    const page = parseInt(params.get('page') || '1', 10);
    const page_size = parseInt(
      params.get('page_size') || `${API_DEFAULT_PAGE_SIZE}`,
      10,
    );
    // const search = params.get('search') || '';

    // 1. Filter
    let filtered = commercialTerms.filter((item) => {
      const matchCustomer = item.customer_uuid === customer_uuid;
      const matchStatus = status ? item.status === status : true;
      const matchCurrency = currency ? item.currency === currency : true;
      const matchNetwork = network ? item.network_name === network : true;
      const matchAgency = matchesAgencyFilter(item.agency_name, agencies);
      /* const matchSearch = search
        ? Object.values(item).some(
            (val) =>
              typeof val === 'string' && val.toLowerCase().includes(search),
          )
        : true; */

      return (
        matchCustomer &&
        matchStatus &&
        matchCurrency &&
        matchNetwork &&
        matchAgency
      );
    });

    // 2. Order
    if (ordering) {
      const isDesc = ordering.startsWith('-');
      const key = (
        isDesc ? ordering.slice(1) : ordering
      ) as TCommercialTermSortBy;

      filtered = filtered.sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return isDesc
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return isDesc ? bValue - aValue : aValue - bValue;
        }

        return 0;
      });
    }

    // 3. Paginate
    const total = filtered.length;
    const pages = Math.ceil(total / page_size);
    const start = Math.max(0, (page - 1) * page_size);
    const end = Math.min(start + page_size, total);
    const results = filtered.slice(start, end);

    // 4. Response
    return res(
      ctx.status(200),
      ctx.json({
        total,
        page,
        pages,
        results,
      }),
    );
  },
);
